import { useMemo, useState } from "react";
import {
  ITEM_CSV_DEFAULT_IDS,
  buildItemsCsv,
  itemCsvColumns,
  itemsCsvFileName,
  type Item,
  type ItemCsvChoice,
  type ItemQuery,
  type StockRecords,
} from "@iltocat/shared";
import { ApiError, api, plural } from "../lib/api.js";
import { allItems } from "../lib/allItems.js";
import { downloadBlob } from "../lib/download.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import { Icon } from "./Icon.js";
import { Notice } from "./Notice.js";
import { Panel } from "./Panel.js";

/**
 * Some of what an inventory knows, as a spreadsheet: the columns somebody
 * picks, in the order they pick them, headed the way the other system wants
 * them. For matching this inventory against an accounting package, an
 * insurer's schedule or a warehouse tool -- the zip export is the "keep
 * everything" half (routes/Export.tsx).
 *
 * Reusable on purpose: it exports whatever `query` matches, so the Export
 * screen mounts it with no query (everything) and the Items screen mounts it
 * with the search that is on screen. Mount it with a `key` that changes with
 * the query; items are read once, when somebody asks to choose columns, and
 * kept so a second download is instant.
 *
 * The last choice is remembered on this device (a convenience, like recent
 * locations: losing it costs a few taps), since somebody matching against the
 * same external system does it monthly.
 */

const STORAGE_KEY = "iltocat.export.csv";

function readSaved(): ItemCsvChoice[] | null {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "null");
    if (!Array.isArray(parsed)) return null;
    const choices = parsed.filter(
      (entry): entry is ItemCsvChoice =>
        typeof entry === "object" &&
        entry !== null &&
        typeof (entry as ItemCsvChoice).id === "string" &&
        typeof (entry as ItemCsvChoice).header === "string",
    );
    return choices.length > 0 ? choices : null;
  } catch {
    return null;
  }
}

function writeSaved(choices: ItemCsvChoice[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(choices));
  } catch {
    // Storage being unavailable costs a remembered choice, not the export.
  }
}

export function ItemCsvExport({
  inventoryId,
  taxonomy,
  query,
  stock = false,
  title = "Just some of the details, as a CSV",
  note = "Pick the columns and name them the way the other system expects, to match this inventory against something else.",
}: {
  inventoryId: string;
  taxonomy: Taxonomy;
  query?: ItemQuery;
  /** A stockroom: offer the stock columns (on hand, client, cost, vendor...). */
  stock?: boolean;
  title?: string;
  note?: string;
}) {
  const [items, setItems] = useState<Item[] | null>(null);
  const [records, setRecords] = useState<StockRecords | undefined>(undefined);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [choices, setChoices] = useState<ItemCsvChoice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const columns = useMemo(() => (items ? itemCsvColumns(items, stock) : []), [items, stock]);
  const byId = useMemo(() => new Map(columns.map((column) => [column.id, column])), [columns]);
  const chosen = useMemo(() => new Set(choices.map((choice) => choice.id)), [choices]);

  async function open() {
    setError(null);
    setDone(null);
    setProgress({ done: 0, total: 0 });
    try {
      const [found, stockRecords] = await Promise.all([
        allItems((loaded, total) => setProgress({ done: loaded, total }), query),
        stock ? api.stock.records() : Promise.resolve(undefined),
      ]);
      const available = new Map(itemCsvColumns(found, stock).map((column) => [column.id, column]));
      const saved = (readSaved() ?? []).filter((choice) => available.has(choice.id));
      setChoices(
        saved.length > 0
          ? saved
          : ITEM_CSV_DEFAULT_IDS.map((id) => ({ id, header: available.get(id)?.label ?? id })),
      );
      setRecords(stockRecords);
      setItems(found);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "The items did not load.");
    } finally {
      setProgress(null);
    }
  }

  const add = (id: string) =>
    setChoices((current) => [...current, { id, header: byId.get(id)?.label ?? id }]);
  const drop = (id: string) => setChoices((current) => current.filter((choice) => choice.id !== id));
  const rename = (id: string, header: string) =>
    setChoices((current) => current.map((choice) => (choice.id === id ? { ...choice, header } : choice)));
  const move = (index: number, by: -1 | 1) =>
    setChoices((current) => {
      const next = [...current];
      const [moved] = next.splice(index, 1);
      if (moved) next.splice(index + by, 0, moved);
      return next;
    });

  function download() {
    if (!items) return;
    setDone(null);
    // A header left blank falls back to the column's own name rather than
    // producing a file whose first row has an empty cell.
    const named = choices.map((choice) => ({
      id: choice.id,
      header: choice.header.trim() || (byId.get(choice.id)?.label ?? choice.id),
    }));
    writeSaved(named);
    const text = buildItemsCsv(items, named, taxonomy, records);
    downloadBlob(new Blob([text], { type: "text/csv;charset=utf-8" }), itemsCsvFileName(inventoryId));
    setDone(`Exported ${plural(items.length, "item")} with ${plural(named.length, "column")}.`);
  }

  const percent = progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : null;
  const unchosen = columns.filter((column) => !chosen.has(column.id));

  return (
    <Panel>
      <div className="feature-card">
        <span className="feature-icon">
          <Icon name="grid" size={24} />
        </span>
        <div className="feature-text">
          <h2 className="panel-title">{title}</h2>
          <p className="muted">{note}</p>
        </div>
      </div>

      {error && <Notice tone="error">{error}</Notice>}
      {done && <Notice tone="ok">{done}</Notice>}

      {progress && (
        <div className="progress" aria-live="polite">
          <div className="progress-row">
            <span>Reading items…</span>
            {progress.total > 0 && (
              <span className="muted">
                {progress.done} of {progress.total}
              </span>
            )}
          </div>
          <div
            className={percent === null ? "meter meter-busy" : "meter"}
            role="progressbar"
            aria-label="Reading items"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent ?? undefined}
          >
            <span style={{ width: `${percent ?? 100}%` }} />
          </div>
        </div>
      )}

      {items && (
        <div className="csv-picker">
          {items.length === 0 && <Notice tone="warn">There are no items to export.</Notice>}

          <ol className="csv-columns" aria-label="Columns in the file, in order">
            {choices.map((choice, index) => {
              const column = byId.get(choice.id);
              return (
                <li key={choice.id} className="csv-column">
                  <input
                    className="input input-small"
                    aria-label={`Header for ${column?.label ?? choice.id}`}
                    placeholder={column?.label}
                    value={choice.header}
                    onChange={(event) => rename(choice.id, event.target.value)}
                  />
                  {column?.private && <span className="pill">Private</span>}
                  <button
                    type="button"
                    className="icon-button icon-button-sm"
                    aria-label={`Move ${column?.label ?? choice.id} up`}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <Icon name="chevron-up" size={16} />
                  </button>
                  <button
                    type="button"
                    className="icon-button icon-button-sm"
                    aria-label={`Move ${column?.label ?? choice.id} down`}
                    disabled={index === choices.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <Icon name="chevron-down" size={16} />
                  </button>
                  <button
                    type="button"
                    className="icon-button icon-button-sm"
                    aria-label={`Leave out ${column?.label ?? choice.id}`}
                    onClick={() => drop(choice.id)}
                  >
                    <Icon name="x" size={16} />
                  </button>
                </li>
              );
            })}
          </ol>

          {unchosen.length > 0 && (
            <>
              <p className="csv-add-label muted">Add a column</p>
              <div className="chip-bar">
                {unchosen.map((column) => (
                  <button
                    key={column.id}
                    type="button"
                    className={column.private ? "chip chip-small chip-private" : "chip chip-small"}
                    onClick={() => add(column.id)}
                  >
                    <Icon name="plus" size={14} />
                    {column.label}
                    {column.private && <span className="sr-only"> (private)</span>}
                  </button>
                ))}
              </div>
            </>
          )}
          <p className="muted csv-private-note">
            Private columns are your own notes and private fields. The file stays on this device, but it is
            yours to share or not once it is downloaded.
          </p>
        </div>
      )}

      <div className="panel-foot">
        {items ? (
          <button
            type="button"
            className="button"
            disabled={choices.length === 0 || items.length === 0}
            onClick={download}
          >
            <Icon name="download" size={18} />
            Download CSV
          </button>
        ) : (
          <button type="button" className="button" disabled={progress !== null} onClick={() => void open()}>
            <Icon name="grid" size={18} />
            {progress ? "Reading…" : "Choose columns"}
          </button>
        )}
      </div>
    </Panel>
  );
}
